import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { MyContext } from "../Context/MyContext";
import { FormPageSkeleton } from "../Components/Skeleton/Skeleton";
import PageLoadGate from "../Components/Skeleton/PageLoadGate";
import { useToast } from "../Components/Toast/ToastProvider";
import { valuationApi } from "../../services/api";
import { useJsonForm, formatMessage } from "../../hooks/useJsonForm";
import "./Valuation.css";

const formatCurrency = (symbol, value) =>
  `${symbol}${Number(value).toLocaleString("en-IN")}`;

export default function Valuation() {
  const navigate = useNavigate();
  const context = useContext(MyContext);
  const { valuation_page: page, valuation_form: formFields } = context;
  const { showToast } = useToast();

  const [formData, setFormData] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const validation = page.validation || {};
  const actions = page.actions || {};
  const resultConfig = page.result || {};

  const { visibleFields, validateForm, renderField } = useJsonForm({
    fields: formFields,
    pageConfig: page,
    formData,
    setFormData,
    context,
    fieldErrors,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      showToast({
        type: "error",
        title: validation.failedTitle,
        message: validation.requiredMessage,
      });
      return;
    }

    setFieldErrors({});
    setSubmitting(true);
    try {
      const res = await valuationApi.estimate(formData);
      setResult(res.data);
    } catch (err) {
      showToast({
        type: "error",
        title: validation.errorTitle,
        message: err.message || validation.errorMessage,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setResult(null);
    setFormData({});
    setFieldErrors({});
  };

  const handleSell = () => {
    navigate(resultConfig.sellCtaPath || "/upload-car", {
      state: { prefill: formData, estimatedPrice: result?.estimatedPrice },
    });
  };

  return (
    <PageLoadGate skeleton={<FormPageSkeleton />} delay={380}>
      <div className="valuation-page">
        <div className="valuation-hero">
          <div className="valuation-hero-inner">
            <span className="valuation-badge">{page.badge}</span>
            <h1>{page.title}</h1>
            <p>{page.subtitle}</p>
          </div>
        </div>

        <div className="valuation-body">
          <div className="valuation-layout">
            <div className="valuation-card content-card">
              {!result ? (
                <form className="valuation-form" onSubmit={handleSubmit} noValidate>
                  <h2 className="valuation-form-title">{page.title}</h2>
                  <p className="valuation-form-subtitle">{page.subtitle}</p>

                  <div className="valuation-form-grid">
                    {visibleFields.map((field) => (
                      <div
                        key={field.name}
                        className={`valuation-field ${field.layout === "full" ? "full-width" : ""} ${
                          fieldErrors[field.name] ? "has-error" : ""
                        }`}
                      >
                        <label className="field-label">
                          {field.label}
                          {field.required && <span className="required-mark">*</span>}
                        </label>
                        {renderField(field)}
                        {field.hint && !fieldErrors[field.name] && (
                          <span className="field-hint">{field.hint}</span>
                        )}
                        {fieldErrors[field.name] && (
                          <span className="field-error">{fieldErrors[field.name]}</span>
                        )}
                      </div>
                    ))}
                  </div>

                  <button className="valuation-btn primary submit" type="submit" disabled={submitting}>
                    {submitting ? actions.submittingLabel : actions.submitLabel}
                  </button>
                </form>
              ) : (
                <div className="valuation-result">
                  <span className="result-icon">💰</span>
                  <h2>{resultConfig.title}</h2>

                  <div className="result-price-block">
                    <span className="result-label">{resultConfig.estimateLabel}</span>
                    <strong className="result-estimate">
                      {formatCurrency(resultConfig.currencySymbol || "₹", result.estimatedPrice)}
                    </strong>
                  </div>

                  <div className="result-range">
                    <span className="result-label">{resultConfig.rangeLabel}</span>
                    <p>
                      {formatCurrency(resultConfig.currencySymbol || "₹", result.minPrice)}
                      {" — "}
                      {formatCurrency(resultConfig.currencySymbol || "₹", result.maxPrice)}
                    </p>
                  </div>

                  <p className="result-confidence">
                    {resultConfig.confidenceLabels?.[result.confidence] || ""}
                  </p>
                  <p className="result-sample">
                    {result.sampleCount > 0
                      ? formatMessage(resultConfig.sampleLabel, { count: result.sampleCount })
                      : resultConfig.noSampleLabel}
                  </p>

                  <div className="valuation-actions">
                    <button type="button" className="valuation-btn primary" onClick={handleSell}>
                      {resultConfig.sellCtaLabel}
                    </button>
                    <button
                      type="button"
                      className="valuation-btn secondary"
                      onClick={() => navigate(resultConfig.browseCtaPath || "/cars")}
                    >
                      {resultConfig.browseCtaLabel}
                    </button>
                    <button type="button" className="valuation-btn ghost" onClick={handleReset}>
                      {resultConfig.resetLabel}
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="valuation-highlights">
              {(page.highlights || []).map((item) => (
                <div key={item.id} className="valuation-highlight">
                  <span className="highlight-icon">{item.icon}</span>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </PageLoadGate>
  );
}
